'use client';

import { cn } from '@/lib/utils';
import { Link, useLocation } from 'react-router-dom';
import {
  PiChalkboardTeacherDuotone,
  PiChalkboardTeacherFill,
} from 'react-icons/pi';
import {
  MdPerson2,
  MdOutlinePerson2,
  MdOutlineGroups,
  MdGroups,
} from 'react-icons/md';
import {
  TbBarbell,
  TbBarbellFilled,
  TbGraph,
  TbGraphFilled,
} from 'react-icons/tb';
import { BiBody } from 'react-icons/bi';
import { useUser } from '@/components/user/user-provider';

const ROUTES = [
  {
    title: 'Planos',
    url: '/plans',
    role: 'user' as const,
    icon: { default: <TbGraph className="w-5 h-5" />, selected: <TbGraphFilled className="w-5 h-5" /> },
  },
  {
    title: 'Avaliação',
    url: '/evaluations',
    role: 'user' as const,
    icon: { default: <BiBody className="w-5 h-5" />, selected: <BiBody className="w-5 h-5" /> },
  },
  {
    title: 'Treinadores',
    url: '/coaches',
    role: 'user' as const,
    icon: {
      default: <PiChalkboardTeacherDuotone className="w-5 h-5" />,
      selected: <PiChalkboardTeacherFill className="w-5 h-5" />,
    },
  },
  {
    title: 'Alunos',
    url: '/customers',
    role: 'coach' as const,
    icon: {
      default: <MdOutlineGroups className="w-5 h-5" />,
      selected: <MdGroups className="w-5 h-5" />,
    },
  },
  {
    title: 'Exercícios',
    url: '/exercises',
    role: 'coach' as const,
    icon: {
      default: <TbBarbell className="w-5 h-5" />,
      selected: <TbBarbellFilled className="w-5 h-5" />,
    },
  },
  {
    title: 'Perfil',
    url: '/profile',
    role: 'all' as const,
    icon: {
      default: <MdOutlinePerson2 className="w-5 h-5" />,
      selected: <MdPerson2 className="w-5 h-5" />,
    },
  },
];

export default function Navbar({ className }: React.ComponentProps<'div'>) {
  const { user } = useUser();
  const data = user ?? { is_coach: false };
  const role = data && !('error' in data) && data.is_coach ? 'coach' : 'user';
  const pathname = useLocation().pathname;

  const visibleRoutes = ROUTES.filter(
    (item) => item.role === 'all' || item.role === role
  );

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 w-full bg-card border-t border-border text-foreground sm:hidden pt-1.5 pb-2 px-3 safe-area-pb',
        className
      )}
    >
      <nav className="flex items-stretch flex-1 min-w-0 gap-0">
        {visibleRoutes.map((item) => {
          const isEvaluations = item.url === '/evaluations';
          const isOnCoachEvaluations = /^\/coaches\/[^/]+\/evaluations/.test(pathname ?? '');
          const isActive =
            (isEvaluations && (pathname?.startsWith('/evaluations') || isOnCoachEvaluations)) ||
            (pathname?.startsWith(item.url) && !(item.url === '/coaches' && isOnCoachEvaluations));
          return (
            <Link
              key={item.url}
              to={item.url}
              aria-label={item.title}
              className={cn(
                'flex flex-col gap-0.5 items-center justify-center flex-1 min-w-0 py-1 px-1 rounded-lg transition-colors',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {isActive ? item.icon.selected : item.icon.default}
              <span className="text-[10px] font-medium truncate w-full text-center">
                {item.title}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
